/**
 * The studio and the player tab, talking.
 *
 * The player lives in a **separate tab of our own origin** rather than inside
 * the studio, and that is the whole design. A cross-origin iframe cannot be
 * captured or drawn to a canvas, so the pixels have to be taken with screen
 * capture - and capture is only worth having if the thing captured is painted
 * at full size, which a panel inside the studio never is.
 *
 * Putting it in its own tab also settles the audio. Capturing the studio's own
 * tab would pull the guests' voices back into the room through the clip's
 * audio track; a tab that plays nothing but the clip has nothing else to leak.
 *
 * Same origin, so a BroadcastChannel is enough - no window handles to keep
 * alive, and it survives the player tab being reloaded.
 */

const YOUTUBE_CHANNEL = 'liveforge.youtube';

/**
 * One channel per player tab.
 *
 * A single shared channel would have every open player answering every
 * command, which rules out the thing a director actually does: line several
 * clips up before the show and cut between them. The key is minted when the
 * tab is opened and travels in its URL, so a player reloaded mid-show comes
 * back on the same channel.
 */
export function youtubeChannel(key: string): string {
  return `${YOUTUBE_CHANNEL}.${key}`;
}

export type YoutubeCommand =
  | { type: 'play' }
  | { type: 'pause' }
  | { type: 'seek'; seconds: number }
  | { type: 'load'; videoId: string }
  /** Asked by the player when it starts, in case the studio opened it first. */
  | { type: 'hello' };

/**
 * Where the picture sits inside the captured tab, in fractions of it.
 *
 * A tab is whatever shape the host's window happens to be, and a capture takes
 * all of it. The player letterboxes itself to 16:9 and reports the box, so the
 * studio can crop the layer to exactly the video - no black bars, whatever the
 * window was doing at the time.
 */
export interface YoutubeFrame {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface YoutubeState {
  type: 'state';
  videoId: string | null;
  ready: boolean;
  playing: boolean;
  position: number;
  duration: number;
  title: string;
  frame: YoutubeFrame;
}

export type YoutubeMessage = YoutubeCommand | YoutubeState;

/**
 * Pulls the id out of whatever the host pasted.
 *
 * Deliberately forgiving: people paste a watch URL, a share link, an embed
 * URL, or the bare id, and refusing four of those to be strict about one is
 * not worth a support conversation.
 */
export function youtubeVideoId(input: string): string | null {
  const trimmed = input.trim();
  if (!trimmed) return null;

  // A bare id: eleven characters of the URL-safe alphabet.
  if (/^[\w-]{11}$/.test(trimmed)) return trimmed;

  try {
    const url = new URL(trimmed);
    const host = url.hostname.replace(/^www\./, '');

    if (host === 'youtu.be') {
      const id = url.pathname.slice(1);
      return /^[\w-]{11}$/.test(id) ? id : null;
    }

    if (host === 'youtube.com' || host === 'm.youtube.com' || host === 'youtube-nocookie.com') {
      const v = url.searchParams.get('v');
      if (v && /^[\w-]{11}$/.test(v)) return v;

      // /embed/<id>, /live/<id>, /shorts/<id>
      const parts = url.pathname.split('/').filter(Boolean);
      const last = parts[parts.length - 1];
      if (last && /^[\w-]{11}$/.test(last)) return last;
    }
  } catch {
    // Not a URL, and not a bare id either.
  }
  return null;
}

/**
 * What a YouTube clip is called, wherever it is shown.
 *
 * The prefix is not decoration: in a layer list beside cameras and captions it
 * says where the picture comes from, and in the capture picker's tab list it is
 * how the host finds the right tab among everything else they have open.
 */
export function youtubeLabel(title: string, videoId: string | null): string {
  const known = title.trim();
  return `YOUTUBE - ${known || videoId || 'video'}`;
}
